import * as React from 'react'
import loadable from '@loadable/component'
import { Property } from 'csstype'

import { HorizontalAlignment } from '@thg-commerce/gravity-theme/alignments'
import { BreakpointArray } from '@thg-commerce/gravity-theme/breakpoints'

import { wrapSwipe } from '../hooks/useSwipe'
import {
  Control,
  ControlsContainer,
  PageIndicatorGridItem,
  PageIndicatorWrapper,
} from '../styles'
import { ChevronIconStyle } from '../theme'
import {
  CarouselButtonPlacement,
  CarouselReducerActions,
  CarouselReducerActionTypes,
} from '../types'

const ChevronLeft = loadable(
  () => import('@thg-commerce/gravity-icons/src/components/ChevronLeft'),
  { ssr: true, fallback: <div style={{ width: 24, height: 24 }} /> },
)
const ChevronRight = loadable(
  () => import('@thg-commerce/gravity-icons/src/components/ChevronRight'),
  { ssr: true, fallback: <div style={{ width: 24, height: 24 }} /> },
)
const PauseIcon = loadable(
  () => import('@thg-commerce/gravity-icons/src/components/Pause'),
  { ssr: true, fallback: <div style={{ width: 24, height: 24 }} /> },
)
const PlayIcon = loadable(
  () => import('@thg-commerce/gravity-icons/src/components/Play'),
  { ssr: true, fallback: <div style={{ width: 24, height: 24 }} /> },
)
const SvgIcon = loadable(
  () => import('@thg-commerce/gravity-icons/src/components/SvgIcon'),
  { ssr: true, fallback: <div style={{ width: 24, height: 24 }} /> },
)

interface ControlsProps {
  showControls: boolean
  autoPlay: boolean
  togglePause: () => void
  i18n: {
    playPauseLabel?: string
    leftScrollLabel?: string
    rightScrollLabel?: string
  }
  removeControlsOnSingleSlide?: boolean
  chevronIconStyle?: ChevronIconStyle
  controls?: {
    visible?: BreakpointArray<boolean>
    size?: number
    alternativeChevronStyling?: boolean
    controlBorderWidth?: string
    controlBorderColour?: Property.Color
    controlDisabledBackgroundColor?: Property.Color
    controlHoverBackgroundColor?: Property.Color
  }
  controlPlacement?: CarouselButtonPlacement
  controlSize?: number
  enableWhiteControls?: boolean
  isPaused: boolean
  currentSlide: number
  totalSlides: number
  loopSlides: boolean
  onNavClick?: (direction: 'left' | 'right') => void
  dispatch: React.Dispatch<CarouselReducerActions>
  disabled: boolean
  previousSlideTo: React.MutableRefObject<number>
  pageIndicators?: JSX.Element[] | null
  enableWrapAround?: boolean
  carouselRef?: React.RefObject<HTMLDivElement>
  highlightPageIndicator?: boolean
}

interface RenderChevronIconProps {
  chevronIconStyle?: ChevronIconStyle
  direction: 'left' | 'right'
}

const RenderChevronIcon: React.FC<RenderChevronIconProps> = ({
  chevronIconStyle,
  direction,
}) => {
  return chevronIconStyle?.chevronIcon?.[direction]?.svgPath ? (
    <SvgIcon
      xmlns="http://www.w3.org/2000/svg"
      viewBox={chevronIconStyle.chevronIcon.viewBox}
      width={chevronIconStyle.chevronIcon.width}
      height={chevronIconStyle.chevronIcon.height}
    >
      <path
        d={chevronIconStyle.chevronIcon[direction].svgPath}
        fillRule="evenodd"
      />
    </SvgIcon>
  ) : direction === 'left' ? (
    <ChevronLeft width="24" height="24" />
  ) : (
    <ChevronRight width="24" height="24" />
  )
}
export const Controls: React.FC<ControlsProps> = ({
  showControls,
  removeControlsOnSingleSlide,
  chevronIconStyle,
  autoPlay,
  togglePause,
  i18n,
  controls,
  controlPlacement,
  controlSize,
  enableWhiteControls,
  isPaused,
  currentSlide,
  totalSlides,
  loopSlides,
  onNavClick,
  dispatch,
  disabled,
  previousSlideTo,
  pageIndicators,
  enableWrapAround,
  carouselRef,
  highlightPageIndicator,
}) => {
  return (
    <ControlsContainer
      hide={!showControls}
      removeControls={removeControlsOnSingleSlide && !showControls}
      controlPlacement={controlPlacement}
    >
      {autoPlay && (
        <Control
          disabled={false}
          onClick={togglePause}
          aria-label={i18n?.playPauseLabel}
          data-testid="carousel-play-button"
          visible={controls?.visible || [true]}
        >
          {isPaused ? (
            <PlayIcon width="24" height="24" />
          ) : (
            <PauseIcon width="24" height="24" />
          )}
        </Control>
      )}
      <Control
        position={HorizontalAlignment.LEFT}
        disabled={disabled}
        onClick={() => {
          if (enableWrapAround && carouselRef) {
            const nextSlide =
              previousSlideTo.current === 0
                ? totalSlides - 1
                : previousSlideTo.current - 1
            wrapSwipe(
              previousSlideTo.current,
              nextSlide,
              totalSlides,
              carouselRef,
            )
            previousSlideTo.current = nextSlide
          } else {
            previousSlideTo.current = previousSlideTo.current - 1
          }
          dispatch({ type: CarouselReducerActionTypes.SlideLeft } as const)
          onNavClick?.('left')
        }}
        aria-label={i18n?.leftScrollLabel}
        data-testid="carousel-control-left"
        visible={controls?.visible || [true]}
        size={controls?.size}
        controlSize={controlSize}
        alternativeDisabledStyling={controls?.alternativeChevronStyling}
        enableWhiteControls={enableWhiteControls}
        controlBorderColour={controls?.controlBorderColour}
        controlBorderWidth={controls?.controlBorderWidth}
        controlHoverBackgroundColor={controls?.controlHoverBackgroundColor}
        controlDisabledBackgroundColor={
          controls?.controlDisabledBackgroundColor
        }
      >
        <RenderChevronIcon
          chevronIconStyle={chevronIconStyle}
          direction={'left'}
        />
      </Control>
      {controlPlacement === CarouselButtonPlacement.BottomMiddle && (
        <PageIndicatorGridItem>
          <PageIndicatorWrapper
            data-testid="page-indicators"
            mobileHorizontalAlignment={HorizontalAlignment.CENTER}
            hide={totalSlides < 2}
            highlightPageIndicator={highlightPageIndicator}
          >
            {pageIndicators}
          </PageIndicatorWrapper>
        </PageIndicatorGridItem>
      )}
      <Control
        position={HorizontalAlignment.RIGHT}
        disabled={
          autoPlay || loopSlides ? false : currentSlide >= totalSlides - 1
        }
        onClick={() => {
          if (enableWrapAround && carouselRef) {
            const nextSlide =
              previousSlideTo.current === totalSlides - 1
                ? 0
                : previousSlideTo.current + 1
            wrapSwipe(
              previousSlideTo.current,
              nextSlide,
              totalSlides,
              carouselRef,
            )
            previousSlideTo.current = nextSlide
          } else {
            previousSlideTo.current = previousSlideTo.current + 1
          }
          dispatch({ type: CarouselReducerActionTypes.SlideRight } as const)
          onNavClick?.('right')
        }}
        aria-label={i18n?.rightScrollLabel}
        data-testid="carousel-control-right"
        visible={controls?.visible || [true]}
        size={controls?.size}
        controlSize={controlSize}
        alternativeDisabledStyling={controls?.alternativeChevronStyling}
        enableWhiteControls={enableWhiteControls}
        controlBorderColour={controls?.controlBorderColour}
        controlBorderWidth={controls?.controlBorderWidth}
        controlHoverBackgroundColor={controls?.controlHoverBackgroundColor}
        controlDisabledBackgroundColor={
          controls?.controlDisabledBackgroundColor
        }
      >
        <RenderChevronIcon
          chevronIconStyle={chevronIconStyle}
          direction={'right'}
        />
      </Control>
    </ControlsContainer>
  )
}
